// Which street map the maps of the app draw on (map-tiles MAP-07, MAP-10, MAP-11; WEB-MAPT-01 … -03): the style of the
// organisation's region in regions.json, light or dark as the system theme. The shells provide it; outside them (the
// widget fixtures) there is none and maps draw on the flat base with no note.
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useSyncExternalStore, type ReactNode } from "react";
import { regionFor, regionsQuery, styleUrl, type Theme } from "../../servers/regions";

export type MapStyle =
  /** regions.json is still loading: wait, so the map is not drawn twice. */
  | { kind: "pending" }
  /** The region's style, and where its satellite style would be (served only when it answers, regions 0.2.1). */
  | { kind: "style"; url: string; satellite?: string }
  /** The region has no street map (or regions.json failed): the flat base and "Street map not available". */
  | { kind: "none" };

export const MapStyleContext = createContext<MapStyle | undefined>(undefined);

/** The map style here; undefined outside a shell. */
export function useMapStyle(): MapStyle | undefined {
  return useContext(MapStyleContext);
}

const DARK = "(prefers-color-scheme: dark)";

function subscribe(onChange: () => void) {
  if (typeof window === "undefined" || !window.matchMedia) return () => undefined;
  const q = window.matchMedia(DARK);
  q.addEventListener("change", onChange);
  return () => q.removeEventListener("change", onChange);
}

/** The system theme, followed while the page is open. */
export function useSystemTheme(): Theme {
  return useSyncExternalStore(
    subscribe,
    () => (typeof window !== "undefined" && window.matchMedia?.(DARK).matches ? "dark" : "light"),
    () => "light",
  );
}

/** Provides the street map of the region `dataRegion` names, or of the server's one open region without it. */
export function MapStyleProvider({ dataRegion, children }: { dataRegion?: string; children: ReactNode }) {
  const regions = useQuery(regionsQuery());
  const theme = useSystemTheme();
  let value: MapStyle;
  if (regions.isPending) value = { kind: "pending" };
  else {
    const region = regions.data && regionFor(regions.data, dataRegion);
    const url = styleUrl(region, window.location.origin, theme);
    const satellite = styleUrl(region, window.location.origin, "satellite");
    value = url ? { kind: "style", url, ...(satellite ? { satellite } : {}) } : { kind: "none" };
  }
  return <MapStyleContext.Provider value={value}>{children}</MapStyleContext.Provider>;
}
